import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTasks, useUsers, useItems } from '../hooks/useQueries';
import DataTable from './Layout/DataTable';
import Toast from './Popup/Toast';
import StatusBadge from './Popup/StatusBadge';
import CreateTaskModal from './CreateTaskModal';
import { formatDate, isOverdue, isDueSoon } from '../utils/dateUtils';
import { formatItemRef } from '../utils/itemIdFormat';
import './TasksDisplay.css';

export default function TasksDisplay({ 
  parentId, 
  parentTitle = '',
  showHeader = true,
  maxHeight = '500px',
  locked = false,
  includeChildTasks = false // New prop to enable child task rollup
}) {
  const navigate = useNavigate();
  
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // React Query hooks
  const { data: allTasks = [], isLoading: loading } = useTasks();
  const { data: users = [] } = useUsers();
  const { data: allItems = [] } = useItems({}, { enabled: includeChildTasks }); // Only fetch items if we need child tasks

  // Get child items when includeChildTasks is enabled
  const childItems = useMemo(() => {
    if (!includeChildTasks || !parentId) return [];
    
    return allItems.filter(item => {
      const childOfValue = item.child_of;
      return childOfValue === parseInt(parentId);
    });
  }, [allItems, parentId, includeChildTasks]);

  // Filter tasks to include both direct tasks and tasks from child items
  const displayTasks = useMemo(() => {
    const directTasks = allTasks.filter(task => task.parent_id === parentId);
    
    if (!includeChildTasks || childItems.length === 0) {
      return directTasks.map(task => ({ ...task, isChildTask: false, sourceItem: null }));
    }
    
    // Get tasks from child items
    const childTaskIds = childItems.map(child => child.id);
    const childTasks = allTasks.filter(task => childTaskIds.includes(task.parent_id));
    
    // Combine and mark the source of each task
    const combinedTasks = [
      ...directTasks.map(task => ({ 
        ...task, 
        isChildTask: false, 
        sourceItem: null 
      })),
      ...childTasks.map(task => {
        const sourceItem = childItems.find(child => child.id === task.parent_id);
        return { 
          ...task, 
          isChildTask: true, 
          sourceItem: sourceItem 
        };
      })
    ];
    
    // Sort by due date, then by source (parent tasks first)
    return combinedTasks.sort((a, b) => {
      // First sort by due date
      const dateA = new Date(a.due_date || '2099-12-31');
      const dateB = new Date(b.due_date || '2099-12-31');
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      
      // Then by task source (parent tasks first)
      if (a.isChildTask !== b.isChildTask) {
        return a.isChildTask ? 1 : -1;
      }
      
      return 0;
    });
  }, [allTasks, parentId, includeChildTasks, childItems]);

  const userNameById = useMemo(() => {
    const map = new Map();
    for (const user of users) {
      if (!user) continue;
      if (user.id == null) continue;
      const key = String(user.id);
      const value = user.name || user.email || String(user.id);
      map.set(key, value);
    }
    return map;
  }, [users]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleCloseToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  const handleCreateNewTask = () => {
    if (locked) return;
    setShowCreateTaskModal(true);
  };


  const handleRowClick = (event, task) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`/tasks/${task?.id}?source=item&sourceItemId=${parentId}`);
  };


  // Define table columns
  const columns = [
    {
      key: 'id',
      label: 'ID',
      className: 'id-cell',
      render: (task) => `#${task.id}`
    },
    {
      key: 'description',
      label: 'Description',
      className: 'description-cell',
      render: (task) => (
        task.description?.length > 80 
          ? `${task.description.substring(0, 80)}...`
          : task.description || '-'
      )
    },
    {
      key: 'due_date',
      label: 'Due Date',
      className: 'date-cell',
      render: (task) => {
        if (!task.due_date) return '-';
        // Completed/canceled tasks are done, so don't flag their due date as overdue/due soon
        const isTaskClosed = task.status === 'Completed' || task.status === 'Canceled';
        const isTaskOverdue = !isTaskClosed && isOverdue(task.due_date);
        const isTaskDueSoon = !isTaskClosed && isDueSoon(task.due_date, 3);
        
        return (
          <span className={`due-date ${isTaskOverdue ? 'overdue' : isTaskDueSoon ? 'due-soon' : ''}`}>
            {formatDate(task.due_date)}
            {isTaskOverdue && <i className="bi bi-exclamation-triangle-fill ms-1"></i>}
            {isTaskDueSoon && !isTaskOverdue && <i className="bi bi-clock-fill ms-1"></i>}
          </span>
        );
      }
    },
    {
      key: 'assigned_to',
      label: 'Assigned To',
      className: 'assigned-cell',
      render: (task) => {
        const byJoinedName = task?.assigned_to_user_name;
        if (byJoinedName) return byJoinedName;

        const candidateId = task?.assigned_to_user_id ?? task?.assigned_to;
        if (candidateId != null) {
          const mapped = userNameById.get(String(candidateId));
          if (mapped) return mapped;
        }

        if (task?.assigned_to == null || task.assigned_to === '') return '-';
        return String(task.assigned_to);
      }
    },
    // Conditionally show source column when including child tasks
    ...(includeChildTasks ? [{
      key: 'source',
      label: 'Source',
      className: 'source-cell',
      render: (task) => {
        if (task.isChildTask && task.sourceItem) {
          return (
            <span 
              className="source-item-link" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/items/${task.sourceItem.id}`);
              }}
              title={`From item: ${task.sourceItem.title}`}
              style={{ 
                color: '#0066cc', 
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.85em'
              }}
            >
              {formatItemRef(task.sourceItem.id)}
            </span>
          );
        }
        return (
          <span style={{ 
            color: '#6c757d', 
            fontSize: '0.85em',
            fontStyle: 'italic' 
          }}>
            Direct
          </span>
        );
      }
    }] : []),
    {
      key: 'status',
      label: 'Status',
      className: 'status-cell',
      render: (task) => <StatusBadge status={task.status} />
    }
  ];


  return (
    <div className="tasks-display">
      {showHeader && (
        <div className="tasks-display-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: '1px solid #dee2e6'
        }}>
          <h5 style={{ margin: 0, color: '#495057' }}>
            Tasks ({displayTasks.length})
            {includeChildTasks && childItems.length > 0 && (
              <span 
                style={{ 
                  fontSize: '0.75em', 
                  color: '#6c757d', 
                  marginLeft: '8px',
                  fontWeight: 'normal'
                }}
              >
                (includes {childItems.length} child item{childItems.length !== 1 ? 's' : ''})
              </span>
            )}
          </h5>
          <button 
            className="btn btn-sm btn-primary"
            onClick={handleCreateNewTask}
            disabled={locked}
            title={locked ? 'This item is locked' : 'Create new task for this item'}
          >
            <i className="bi bi-plus-lg"></i>
            New Task
          </button>
        </div>
      )}
      
      <div className="tasks-display-table" style={{ maxHeight }}>
        <DataTable
          columns={columns}
          data={displayTasks}
          loading={loading}
          sortConfig={{ key: 'created_on', direction: 'desc' }}
          onSort={() => {}} // No sorting for embedded view
          onRowClick={handleRowClick}
          emptyMessage={
            includeChildTasks && childItems.length > 0 
              ? `No tasks found for this item or its ${childItems.length} child item${childItems.length !== 1 ? 's' : ''}`
              : "No tasks found for this item"
          }
        />
      </div>

      {showCreateTaskModal && (
        <CreateTaskModal
          show={showCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
          preSelectedParentId={parentId}
          preSelectedParentTitle={parentTitle}
          onTaskCreated={(message, type) => showToast(message, type)}
          taskDetailUrlBuilder={(taskId) => `/tasks/${taskId}?source=item&sourceItemId=${parentId}`}
        />
      )}

      {toast.show && createPortal(
        <Toast 
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
        />,
        document.body
      )}

    </div>
  );
}